import React, { useState } from 'react';
import { Typography, Grid, Card, CardContent, Select, MenuItem, FormControl, InputLabel, Box } from '@mui/material';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { League } from '../../../../../sdk/model';
import { ChartDataPoint, COLORS } from '../types';

interface RosterManagementChartsProps {
  league: League;
  chartData: ChartDataPoint[];
}

// Custom tooltip for roster charts
const RosterTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div style={{
        backgroundColor: '#1565c0',
        padding: '10px',
        borderRadius: '6px',
        color: 'white',
        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
        minWidth: '160px',
        fontSize: '11px'
      }}>
        <p style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '12px' }}>
          {data.name || data.fullName || label}
        </p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ margin: '2px 0', color: entry.color }}>
            <strong>{entry.name}:</strong> {entry.value}
            {entry.name.includes('Salary') && '$'}{entry.name.includes('%') && '%'}
          </p>
        ))}
        {data.deadspace && data.deadspace > 0 && (
          <p style={{ margin: '2px 0', color: '#ff4444' }}>
            <strong>Deadspace:</strong> ${data.deadspace.toLocaleString()}
          </p>
        )}
        {data.activeSalary && (
          <p style={{ margin: '2px 0', color: '#ff7300' }}>
            <strong>Active Salary:</strong> ${data.activeSalary.toLocaleString()}
          </p>
        )}
      </div>
    );
  }
  return null;
};

// Custom X-axis component for roster charts that renders team icons
const RosterTeamIconXAxis = ({ x, y, payload, chartData }: any) => {
  if (!payload || !payload.value) return null;
  
  // Find the team data based on the team name
  const teamName = payload.value;
  const teamDataItem = chartData?.find((item: any) => item.name === teamName || item.fullName === teamName);
  const iconUrl = teamDataItem?.teamData?.picture;
  
  if (!iconUrl) {
    return (
      <g>
        <text x={x} y={y + 4} fill="#666" textAnchor="middle" fontSize="10">
          {teamDataItem?.teamData?.shortName || teamName || 'Team'}
        </text>
      </g>
    );
  }

  return (
    <g>
      <foreignObject x={x - 10} y={y - 8} width="20" height="20">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <img 
            src={iconUrl} 
            alt={teamDataItem?.fullName || teamName}
            style={{ 
              width: '18px', 
              height: '18px', 
              borderRadius: '50%',
              objectFit: 'cover'
            }}
            onError={(e) => {
              // Fallback to text if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                parent.innerHTML = `<div style="font-size: 8px; color: #666; text-align: center; line-height: 20px;">${teamDataItem?.teamData?.shortName || teamName || 'Team'}</div>`;
              }
            }}
          />
        </div>
      </foreignObject>
    </g>
  );
};

// Color palette for pie charts
const PIE_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00c49f', '#ffbb28', '#ff8042'];

// Custom label component for pie charts with smaller text
const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, value, index }: any) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text 
      x={x} 
      y={y} 
      fill="white" 
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
      fontSize="9px"
      fontWeight="bold"
    >
      {`${name}: ${value}`}
    </text>
  );
};

// Custom label component for salary pie charts with currency formatting (positioned outside)
const SalaryPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, value, index }: any) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 15; // Position outside the pie
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text 
      x={x} 
      y={y} 
      fill="white" 
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
      fontSize="9px"
      fontWeight="bold"
    >
      {`${name}: $${value.toLocaleString()}`}
    </text>
  );
};

// Custom label component for percentage pie charts (positioned outside)
const PercentPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, value, index }: any) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 15; // Position outside the pie
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text 
      x={x} 
      y={y} 
      fill="white" 
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
      fontSize="9px"
      fontWeight="bold"
    >
      {`${name}: ${value}%`}
    </text>
  );
};

// Enhanced tooltip for salary by position pie chart
const SalaryPositionTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div style={{
        backgroundColor: '#1565c0',
        padding: '10px',
        borderRadius: '6px',
        color: 'white',
        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
        minWidth: '160px',
        fontSize: '11px'
      }}>
        <p style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '12px' }}>
          Position: {data.name}
        </p>
        <p style={{ margin: '2px 0', color: data.color }}>
          <strong>Salary Allocation:</strong> ${data.value.toLocaleString()}
        </p>
        <p style={{ margin: '2px 0', fontSize: '10px', opacity: 0.8 }}>
          <em>Estimated based on roster composition</em>
        </p>
      </div>
    );
  }
  return null;
};

// Enhanced tooltip for salary cap usage pie chart
const CapUsageTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    let description = '';
    
    if (data.name === 'Active Salary') {
      description = 'Salary for players currently on roster';
    } else if (data.name === 'Deadspace') {
      description = 'Salary for players no longer on roster';
    } else if (data.name === 'Available') {
      description = 'Remaining salary cap space';
    }
    
    return (
      <div style={{
        backgroundColor: '#1565c0',
        padding: '10px',
        borderRadius: '6px',
        color: 'white',
        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
        minWidth: '180px',
        fontSize: '11px'
      }}>
        <p style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '12px' }}>
          {data.name}
        </p>
        <p style={{ margin: '2px 0', color: data.color }}>
          <strong>Percentage:</strong> {data.value}% of salary cap
        </p>
        <p style={{ margin: '2px 0', fontSize: '10px', opacity: 0.8 }}>
          <em>{description}</em>
        </p>
      </div>
    );
  }
  return null;
};

// Enhanced tooltip specifically for salary cap utilization chart
const SalaryCapTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const activeSalary = data.activeSalary || 0;
    const deadspace = data.deadspace || 0;
    const totalSalary = data.totalSalary || 0;
    const remainingCap = data.remainingCap || 0;
    
    return (
      <div style={{
        backgroundColor: '#1565c0',
        padding: '10px',
        borderRadius: '6px',
        color: 'white',
        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
        minWidth: '200px',
        fontSize: '11px'
      }}>
        <p style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '12px' }}>
          {data.fullName || label}
        </p>
        <p style={{ margin: '2px 0', color: '#ff7300' }}>
          <strong>Active Salary:</strong> ${activeSalary.toLocaleString()} ({data.activeSalaryPercent}%)
        </p>
        {deadspace > 0 && (
          <p style={{ margin: '2px 0', color: '#ff4444' }}>
            <strong>Dead Cap:</strong> ${deadspace.toLocaleString()} ({data.deadspacePercent}%)
          </p>
        )}
        <p style={{ margin: '2px 0', color: '#82ca9d' }}>
          <strong>Available Cap:</strong> ${remainingCap.toLocaleString()}
        </p>
        <div style={{ borderTop: '1px solid #666', paddingTop: '4px', marginTop: '4px' }}>
          <p style={{ margin: '2px 0', fontSize: '10px' }}>
            <strong>Total Used:</strong> ${totalSalary.toLocaleString()} ({data.salaryCapUsed}%)
          </p>
        </div>
      </div>
    );
  }
  return null;
};

export function RosterManagementCharts({ league, chartData }: RosterManagementChartsProps) {
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  // Calculate salary cap utilization data with deadspace
  const salaryCapData = league.teams?.map(team => {
    const currentSeasonPayments = team.paymentsBySeason?.[league.currentSeason?.seasonYear?.toString() || ''] || [];
    const totalSalary = currentSeasonPayments.reduce((sum, paymentSeason) => {
      const seasonTotal = paymentSeason.payments?.reduce((seasonSum, payment) => 
        seasonSum + (payment.price || 0), 0) || 0;
      return sum + seasonTotal;
    }, 0);
    
    // Calculate deadspace - salary spent on players not currently on roster
    // This is an approximation since we'd need to match payment contracts to current players
    const totalRosterCount = Object.entries(team.playerRoster || {})
      .filter(([position]) => position !== 'IR' && position !== 'Practice' && position !== 'PRACTICE')
      .reduce((sum, [_, players]) => sum + (players?.length || 0), 0);
    
    // Estimate active salary vs deadspace (this is simplified - real calculation would need player-contract mapping)
    const paymentCount = currentSeasonPayments.reduce((sum, paymentSeason) => 
      sum + (paymentSeason.payments?.length || 0), 0);
    
    const estimatedDeadspace = paymentCount > totalRosterCount ? 
      Math.round(totalSalary * Math.min(0.2, (paymentCount - totalRosterCount) / paymentCount)) : 0;
    
    const activeSalary = totalSalary - estimatedDeadspace;
    const salaryCapUsed = Math.round((totalSalary / (league.currentSeason?.nflSeason?.salaryCap || 1)) * 100);
    
    return {
      name: team.name?.length! > 8 ? team.name?.substring(0, 8) + '...' : team.name,
      fullName: team.name,
      salaryCapUsed: Math.min(salaryCapUsed, 100),
      totalSalary: Math.round(totalSalary),
      activeSalary: Math.round(activeSalary),
      deadspace: estimatedDeadspace,
      activeSalaryPercent: Math.round((activeSalary / (league.currentSeason?.nflSeason?.salaryCap || 1)) * 100),
      deadspacePercent: Math.round((estimatedDeadspace / (league.currentSeason?.nflSeason?.salaryCap || 1)) * 100),
      remainingCap: Math.max((league.currentSeason?.nflSeason?.salaryCap || 0) - totalSalary, 0),
      teamData: {
        picture: team.picture,
        fullName: team.name,
        shortName: team.name?.length! > 6 ? team.name?.substring(0, 6) : team.name
      }
    };
  }) || [];
  salaryCapData.sort((a, b) => b.salaryCapUsed - a.salaryCapUsed);

  // Calculate roster composition by position (Active + Bench, excluding IR/Practice)
  const rosterCompositionData = league.teams?.map(team => {
    const positionCounts = {
      QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DEF: 0
    };
    
    Object.entries(team.playerRoster || {}).forEach(([position, players]) => {
      const count = players?.length || 0;
      // Include all positions except IR and Practice squad
      if (position !== 'IR' && position !== 'Practice' && position !== 'PRACTICE') {
        if (positionCounts.hasOwnProperty(position)) {
          positionCounts[position] += count;
        } else if (position === 'BN') {
          // For bench players, we need to count them by their actual positions
          // Since we don't have position data for bench, distribute evenly or skip detailed breakdown
          // For now, we'll add them to a general "Bench" category or distribute proportionally
          // But since the user wants position breakdown, let's assume bench players maintain position ratios
          const totalActive = Object.values(positionCounts).reduce((sum, c) => sum + c, 0);
          if (totalActive > 0) {
            // Distribute bench players proportionally to active positions
            Object.keys(positionCounts).forEach(pos => {
              const proportion = positionCounts[pos] / totalActive;
              positionCounts[pos] += Math.round(count * proportion);
            });
          }
        }
      }
    });

    return {
      name: team.name?.length! > 8 ? team.name?.substring(0, 8) + '...' : team.name,
      fullName: team.name,
      ...positionCounts,
      totalActive: Object.values(positionCounts).reduce((sum, count) => sum + count, 0),
      teamData: {
        picture: team.picture,
        fullName: team.name,
        shortName: team.name?.length! > 6 ? team.name?.substring(0, 6) : team.name
      }
    };
  }) || [];
  rosterCompositionData.sort((a, b) => b.totalActive - a.totalActive);

  // Calculate salary spending by position
  const salaryByPositionData = league.teams?.map(team => {
    const currentSeasonPayments = team.paymentsBySeason?.[league.currentSeason?.seasonYear?.toString() || ''] || [];
    const positionSalaries = {
      QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DEF: 0
    };
    
    // Sum up salary for each position (this is simplified - we'd need player position data)
    // For now, distribute evenly across positions based on roster counts
    const totalSalary = currentSeasonPayments.reduce((sum, paymentSeason) => {
      const seasonTotal = paymentSeason.payments?.reduce((seasonSum, payment) => 
        seasonSum + (payment.price || 0), 0) || 0;
      return sum + seasonTotal;
    }, 0);

    // Simple distribution based on roster composition (in reality you'd match player positions to salaries)
    const rosterCounts = rosterCompositionData.find(r => r.fullName === team.name);
    const totalActiveRoster = rosterCounts?.totalActive || 1;
    
    if (rosterCounts) {
      Object.keys(positionSalaries).forEach(pos => {
        const positionCount = rosterCounts[pos] || 0;
        positionSalaries[pos] = Math.round((totalSalary * positionCount) / totalActiveRoster);
      });
    }

    return {
      name: team.name?.length! > 8 ? team.name?.substring(0, 8) + '...' : team.name,
      fullName: team.name,
      ...positionSalaries,
      totalSalary,
      teamData: {
        picture: team.picture,
        fullName: team.name,
        shortName: team.name?.length! > 6 ? team.name?.substring(0, 6) : team.name
      }
    };
  }) || [];
  salaryByPositionData.sort((a, b) => b.totalSalary - a.totalSalary);

  // Get individual team data for pie charts
  const getSelectedTeamData = () => {
    if (!selectedTeamId) return null;
    return league.teams?.find(team => team.id === selectedTeamId);
  };

  const getTeamRosterPieData = () => {
    const team = getSelectedTeamData();
    if (!team) return [];
    
    const positionCounts = { QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DEF: 0 };
    Object.entries(team.playerRoster || {}).forEach(([position, players]) => {
      const count = players?.length || 0;
      // Include all positions except IR and Practice squad
      if (position !== 'IR' && position !== 'Practice' && position !== 'PRACTICE') {
        if (positionCounts.hasOwnProperty(position)) {
          positionCounts[position] += count;
        } else if (position === 'BN') {
          // Distribute bench players proportionally to active positions
          const totalActive = Object.values(positionCounts).reduce((sum, c) => sum + c, 0);
          if (totalActive > 0) {
            Object.keys(positionCounts).forEach(pos => {
              const proportion = positionCounts[pos] / totalActive;
              positionCounts[pos] += Math.round(count * proportion);
            });
          }
        }
      }
    });
    
    return Object.entries(positionCounts)
      .filter(([_, count]) => count > 0)
      .map(([position, count]) => ({ name: position, value: count }));
  };

  const getTeamSalaryPieData = () => {
    const team = getSelectedTeamData();
    if (!team) return [];
    
    const currentSeasonPayments = team.paymentsBySeason?.[league.currentSeason?.seasonYear?.toString() || ''] || [];
    const totalSalary = currentSeasonPayments.reduce((sum, paymentSeason) => {
      const seasonTotal = paymentSeason.payments?.reduce((seasonSum, payment) => 
        seasonSum + (payment.price || 0), 0) || 0;
      return sum + seasonTotal;
    }, 0);

    const rosterCounts = rosterCompositionData.find(r => r.fullName === team.name);
    const totalActiveRoster = rosterCounts?.totalActive || 1;
    
    const positionSalaries = { QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DEF: 0 };
    if (rosterCounts) {
      Object.keys(positionSalaries).forEach(pos => {
        const positionCount = rosterCounts[pos] || 0;
        positionSalaries[pos] = Math.round((totalSalary * positionCount) / totalActiveRoster);
      });
    }
    
    return Object.entries(positionSalaries)
      .filter(([_, salary]) => salary > 0)
      .map(([position, salary]) => ({ name: position, value: salary }));
  };

  const getTeamCapUtilizationPieData = () => {
    const team = getSelectedTeamData();
    if (!team) return [];
    
    const teamCapData = salaryCapData.find(data => data.fullName === team.name);
    if (!teamCapData) return [];
    
    const salaryCap = league.currentSeason?.nflSeason?.salaryCap || 1;
    const activePercent = Math.round((teamCapData.activeSalary / salaryCap) * 100);
    const deadspacePercent = Math.round((teamCapData.deadspace / salaryCap) * 100);
    const availablePercent = 100 - teamCapData.salaryCapUsed;
    
    const pieData = [
      { name: 'Active Salary', value: activePercent },
      { name: 'Available', value: availablePercent }
    ];
    
    if (deadspacePercent > 0) {
      pieData.splice(1, 0, { name: 'Deadspace', value: deadspacePercent });
    }
    
    return pieData;
  };

  // Removed static position allocation chart as it doesn't provide dynamic insights

  return (
    <Grid container spacing={3}>
      {/* Individual Team Analysis Section - Moved to Top */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Individual Team Analysis</Typography>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Select Team</InputLabel>
                <Select
                  value={selectedTeamId}
                  label="Select Team"
                  onChange={(e) => setSelectedTeamId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Choose a team...</em>
                  </MenuItem>
                  {league.teams?.map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {selectedTeamId && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Roster Composition</Typography>
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={getTeamRosterPieData()}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={<PieLabel />}
                            outerRadius={70}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {getTeamRosterPieData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Salary by Position</Typography>
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={getTeamSalaryPieData()}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={<SalaryPieLabel />}
                            outerRadius={70}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {getTeamSalaryPieData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip content={<SalaryPositionTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Salary Cap Usage</Typography>
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={getTeamCapUtilizationPieData()}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={<PercentPieLabel />}
                            outerRadius={70}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {getTeamCapUtilizationPieData().map((entry, index) => {
                              let color = '#82ca9d'; // Default: Available (green)
                              if (entry.name === 'Active Salary') color = '#ff7300'; // Orange
                              if (entry.name === 'Deadspace') color = '#ff4444'; // Red
                              return (
                                <Cell key={`cell-${index}`} fill={color} />
                              );
                            })}
                          </Pie>
                          <Tooltip content={<CapUsageTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* League Analysis Section */}
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom sx={{ mt: 3, mb: 2 }}>League Analysis</Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Salary Cap Utilization</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={salaryCapData} margin={{ bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={<RosterTeamIconXAxis chartData={salaryCapData} />}
                  height={40}
                  interval={0}
                />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Tooltip content={<SalaryCapTooltip />} />
                <Bar dataKey="activeSalaryPercent" stackId="a" fill="#ff7300" name="Active Salary %" />
                <Bar dataKey="deadspacePercent" stackId="a" fill="#ff4444" name="Dead Cap %" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Roster Composition by Position</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={rosterCompositionData} margin={{ bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={<RosterTeamIconXAxis chartData={rosterCompositionData} />}
                  height={40}
                  interval={0}
                />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip content={<RosterTooltip />} />
                <Bar dataKey="QB" stackId="a" fill="#8884d8" name="QB" />
                <Bar dataKey="RB" stackId="a" fill="#82ca9d" name="RB" />
                <Bar dataKey="WR" stackId="a" fill="#ffc658" name="WR" />
                <Bar dataKey="TE" stackId="a" fill="#ff7300" name="TE" />
                <Bar dataKey="K" stackId="a" fill="#0088fe" name="K" />
                <Bar dataKey="DEF" stackId="a" fill="#00c49f" name="DEF" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Salary Spending by Position</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={salaryByPositionData} margin={{ bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={<RosterTeamIconXAxis chartData={salaryByPositionData} />}
                  height={40}
                  interval={0}
                />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip 
                  content={<RosterTooltip />}
                  formatter={(value, name) => [`$${(value as number).toLocaleString()}`, name]}
                />
                <Bar dataKey="QB" stackId="a" fill="#8884d8" name="QB" />
                <Bar dataKey="RB" stackId="a" fill="#82ca9d" name="RB" />
                <Bar dataKey="WR" stackId="a" fill="#ffc658" name="WR" />
                <Bar dataKey="TE" stackId="a" fill="#ff7300" name="TE" />
                <Bar dataKey="K" stackId="a" fill="#0088fe" name="K" />
                <Bar dataKey="DEF" stackId="a" fill="#00c49f" name="DEF" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
